import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Trash2 } from 'lucide-react';
import { API } from '../App';
import { getSiteProfile } from '../lib/site-profile';
import { addToCart, cartTotal, readCart, removeFromCart } from '../lib/cheep-cart';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { getLockedProductImage, getDefaultVariationSelection } from '../lib/variationImageLock';
import { normalizeCatalogProduct } from '../lib/catalogProduct';

export default function CartPage() {
  const site = getSiteProfile();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState(() => readCart());
  const [loadingAdd, setLoadingAdd] = useState(false);

  const action = searchParams.get('action');
  const addId = searchParams.get('id');

  useEffect(() => {
    if (action !== 'add' || !addId) return;

    let cancelled = false;
    const loadAndAdd = async () => {
      setLoadingAdd(true);
      try {
        let product = null;
        try {
          const live = await axios.get(`${API}/products/${encodeURIComponent(addId)}`, {
            params: { destination: 'BB', currency: 'USD' },
          });
          product = normalizeCatalogProduct(live.data, { destination: 'BB', currency: 'USD' });
        } catch {
          const response = await axios.get(`${API}/cheepsweepz/catalog/product/${encodeURIComponent(addId)}`);
          product = normalizeCatalogProduct(response.data?.product, { destination: 'BB', currency: 'USD' });
        }
        if (cancelled) return;
        if (!product) {
          toast.error('Product not found for checkout.');
          return;
        }
        const lockedImage =
          getLockedProductImage(product, getDefaultVariationSelection(product?.variation_matrix)) ||
          product.real_images?.[0] ||
          product.images?.[0];
        const cart = addToCart({
          product_id: product.product_id,
          title: product.name,
          list_price: product.landed_price || product.list_price,
          image_url: lockedImage,
          marketplace: product.marketplace,
          variation_matrix: product.variation_matrix,
        });
        setItems(cart);
        toast.success('Added to your checkout session.');
        setSearchParams({});
      } catch (error) {
        if (!cancelled) {
          toast.error(error.response?.data?.detail || 'Could not load product for cart.');
        }
      } finally {
        if (!cancelled) setLoadingAdd(false);
      }
    };

    loadAndAdd();
    return () => {
      cancelled = true;
    };
  }, [action, addId, setSearchParams]);

  const total = useMemo(() => cartTotal(items), [items]);

  const handleRemove = (productId) => {
    setItems(removeFromCart(productId));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <div className="mx-auto max-w-3xl px-4 py-10">
        <Link to="/submit" className="mb-8 inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white">
          <ArrowLeft className="h-4 w-4" />
          Submit another link
        </Link>

        <div className="mb-6 flex items-center gap-3">
          <div className="rounded-2xl bg-orange-500/20 p-3 text-orange-400">
            <ShoppingCart className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-300">Checkout</p>
            <h1 className="text-2xl font-bold">{site.brandName} cart</h1>
          </div>
        </div>

        {loadingAdd ? (
          <p className="text-slate-300">Adding your extracted product...</p>
        ) : null}

        {items.length === 0 ? (
          <div className="rounded-3xl border border-white/10 bg-white/5 p-8 text-center">
            <p className="text-slate-300">Your cart is empty.</p>
            <Button className="mt-4 rounded-2xl bg-orange-500 hover:bg-orange-600" onClick={() => navigate('/submit')}>
              Submit a product link
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((item) => (
              <div key={item.product_id} className="flex gap-4 rounded-2xl border border-white/10 bg-white/5 p-4">
                {item.image_url ? (
                  <img src={item.image_url} alt={item.title} className="h-20 w-20 rounded-xl object-cover" />
                ) : (
                  <div className="flex h-20 w-20 items-center justify-center rounded-xl bg-slate-800 text-xs">No image</div>
                )}
                <div className="flex-1">
                  <p className="font-semibold text-white">{item.title}</p>
                  <p className="text-xs uppercase tracking-wide text-slate-400">{item.marketplace}</p>
                  <p className="mt-2 text-emerald-300">${Number(item.list_price).toFixed(2)}</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(item.product_id)}
                  className="text-slate-400 hover:text-red-300"
                  aria-label="Remove item"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            ))}

            <div className="rounded-2xl border border-white/10 bg-slate-950/60 p-4">
              <div className="flex items-center justify-between text-lg font-semibold">
                <span>Total</span>
                <span className="text-emerald-300">${total.toFixed(2)}</span>
              </div>
              <p className="mt-2 text-xs text-slate-400">Includes 20% corporate margin and customs estimate.</p>
              <Button className="mt-4 h-12 w-full rounded-2xl bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700">
                Proceed to checkout
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
